import {identity, isString} from 'lodash';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {FrontMouseEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {CellsHeaderFiltersProps, renderFilterName} from '../../../cells/header/cellsHeaderFilters';
import {
  computeInboxesSearchBarButtonBackgroundColor,
  InboxesSearchBarButtonStyleProps,
} from '../queryBuilder/inboxesSearchQueryBuilderButton';
import {InboxesSearchLocationDropdown} from './inboxesSearchFilterDropdown';

/*
 * Props.
 */

interface InboxesSearchFiltersProps {
  isOpen: boolean;
  onToggleMouseDown: (event: FrontMouseEvent) => void;
  onToggleClick: () => void;
  isDisabled: boolean;
  hasFocus: boolean;
  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  selectedFilter: InboxesSearchLocationsEnum;
  onSearchFilterSelect: (arg: InboxesSearchLocationsEnum) => void;
}

/*
 * Styles.
 */

const StyledFiltersButton = styled(ChromelessButton)<InboxesSearchBarButtonStyleProps>`
  display: flex;

  ${(p) => computeInboxesSearchBarButtonBackgroundColor(p.$hasFocus, p.$isOpen)}
`;

const StyledFiltersContent = styled.div<{$hasFocus: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;

  margin: 5px 0;
  padding: 0 8px;

  border-right: 1px solid ${(p) => (p.$hasFocus ? p.theme.greys.shade30 : p.theme.alphas.black40)};
`;

const StyledFilterNameDiv = styled.div<{$hasFocus: boolean}>`
  margin-right: 8px;
  font-size: ${fontSizes.medium};
  color: ${(p) => (p.$hasFocus ? p.theme.greys.shade90 : p.theme.greys.white)};
  white-space: nowrap;
`;

/*
 * Component.
 */

export const InboxesSearchFilters: FC<InboxesSearchFiltersProps> = ({
  isOpen,
  onToggleMouseDown,
  onToggleClick,
  isDisabled,
  hasFocus,
  searchFilters,
  selectedFilter,
  onSearchFilterSelect,
}) => {
  const theme = useUserTheme();

  const {localSearchFilterName} = searchFilters;

  const buttonRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <StyledFiltersButton
        $hasFocus={hasFocus}
        $isOpen={isOpen}
        ref={buttonRef}
        hasHover={isOpen}
        isOpen={isOpen}
        isDisabled={isDisabled}
        onMouseDown={onToggleMouseDown}
        onClick={onToggleClick}
      >
        <StyledFiltersContent $hasFocus={hasFocus}>
          <StyledFilterNameDiv $hasFocus={hasFocus}>
            {maybeTruncate(renderFilterName(selectedFilter, localSearchFilterName))}
          </StyledFilterNameDiv>
          <Icon name="chevronDownSmall" color={hasFocus ? theme.greys.shade70 : theme.greys.white} />
        </StyledFiltersContent>
      </StyledFiltersButton>
      {isOpen && (
        <InboxesSearchLocationDropdown
          searchFilters={searchFilters}
          selectedFilter={selectedFilter}
          onSearchFilterSelect={onSearchFilterSelect}
          popoverProps={{anchor: buttonRef, onRequestClose: onToggleClick, wrap: identity}}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeTruncate(filterName: ReactNode) {
  if (!filterName) {
    return null;
  }

  if (!isString(filterName)) {
    return filterName;
  }

  const shortName = filterName.length > 14 ? `${filterName.slice(0, 14)}…` : filterName;
  return shortName;
}
